import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, message, Tag, Spin, Button, Modal, Form, Input, Select, Popconfirm, Switch, InputNumber, Space } from 'antd';
import { FileTextOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined, PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { tradeAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const Topics = () => {
  const [topics, setTopics] = useState([]);
  const [traderProfiles, setTraderProfiles] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [authMethods, setAuthMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [deletingTopic, setDeletingTopic] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTopics();
    fetchTraderProfiles();
    fetchDataSources();
    fetchAuthMethods();
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const fetchTopics = async () => {
    try {
      const response = await tradeAPI.getTopics();
      const allData = response.data || [];
      const total = allData.length;

      // 前端分页计算
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const currentPageData = allData.slice(startIndex, endIndex);

      setTopics(currentPageData);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      message.error('获取交易话题失败');
      console.error('获取交易话题失败:', error);
      setTopics([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  const fetchTraderProfiles = async () => {
    try {
      const response = await tradeAPI.getTraderProfiles();
      setTraderProfiles(response.data);
    } catch (error) {
      console.error('获取交易员资料失败:', error);
    }
  };

  const fetchAuthMethods = async () => {
    try {
      const response = await tradeAPI.getAuthMethods();
      setAuthMethods(response.data);
    } catch (error) {
      console.error('获取认证方法失败:', error);
    }
  };

  const fetchDataSources = async () => {
    try {
      const response = await tradeAPI.getDataSources();
      setDataSources(response.data);
    } catch (error) {
      console.error('获取数据源失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTopic = () => {
    setEditingTopic(null);
    setIsModalVisible(true);
  };

  const handleEditTopic = (record) => {
    setEditingTopic(record);
    setIsModalVisible(true);
    setTimeout(() => {
      form.setFieldsValue(record);
    }, 0);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingTopic) {
        // 编辑交易话题
        await tradeAPI.updateTopic(editingTopic.id, values);
        message.success('交易话题更新成功');
      } else {
        // 创建交易话题
        await tradeAPI.createTopic(values);
        message.success('交易话题创建成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchTopics();
    } catch (error) {
      // 处理错误响应，提取错误信息
      let errorMessage = '操作失败';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic 验证错误数组，取第一个错误的信息
          errorMessage = detail[0].msg || '验证失败';
        } else if (typeof detail === 'object') {
          errorMessage = '请求参数有误';
        }
      }
      message.error(errorMessage);
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingTopic(null);
    form.resetFields();
  };

  const handleDeleteTopic = (topic) => {
    setDeletingTopic(topic);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTopic) return;

    try {
      await tradeAPI.deleteTopic(deletingTopic.id);
      message.success('交易话题删除成功');
      setIsDeleteModalVisible(false);
      setDeletingTopic(null);
      fetchTopics();
    } catch (error) {
      message.error('删除话题失败');
      console.error('删除话题失败:', error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingTopic(null);
  };

  const handleEnableSync = async (topic) => {
    try {
      await tradeAPI.updateTopicSchedule(topic.id, { enabled: true, interval_minutes: topic.schedule_interval_minutes });
      message.success(`已启用话题 "${topic.name}" 的自动同步`);
      fetchTopics();
    } catch (error) {
      message.error(error.response?.data?.detail || '启用自动同步失败');
      console.error('启用自动同步失败:', error);
    }
  };

  const handleDisableSync = async (topic) => {
    try {
      await tradeAPI.updateTopicSchedule(topic.id, { enabled: false, interval_minutes: topic.schedule_interval_minutes });
      message.success(`已禁用话题 "${topic.name}" 的自动同步`);
      fetchTopics();
    } catch (error) {
      message.error(error.response?.data?.detail || '禁用自动同步失败');
      console.error('禁用自动同步失败:', error);
    }
  };

  const handleManualSync = async (topic) => {
    try {
      await tradeAPI.manualSyncTopic(topic.id);
      message.success(`已触发话题 "${topic.name}" 的手动同步`);
      fetchTopics();
    } catch (error) {
      message.error(error.response?.data?.detail || '手动同步失败');
      console.error('手动同步失败:', error);
    }
  };

  const getTraderName = (traderId) => {
    const trader = traderProfiles.find((tp) => tp.id === traderId);
    return trader ? trader.display_name || `交易员 ${traderId}` : `交易员 ${traderId}`;
  };

  const getDataSourceName = (sourceId) => {
    const dataSource = dataSources.find((ds) => ds.id === sourceId);
    return dataSource ? dataSource.name : '未知数据源';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '交易员',
      dataIndex: 'trader_id',
      key: 'trader_id',
      render: (traderId) => getTraderName(traderId),
      width: 120,
    },
    {
      title: '数据源',
      dataIndex: 'source_id',
      key: 'source_id',
      render: (sourceId) => getDataSourceName(sourceId),
      width: 200,
    },
    {
      title: '外部话题ID',
      dataIndex: 'external_topic_id',
      key: 'external_topic_id',
      render: (externalId) => externalId || '-',
      width: 120,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (description) => description || '-',
      width: 120,
    },
    {
      title: '市场类型',
      dataIndex: 'market_type',
      key: 'market_type',
      width: 100,
      render: (marketType) => {
        if (!marketType) return '-';
        const marketMap = { US: '美股', HK: '港股', CN: 'A股' };
        return marketMap[marketType] || marketType;
      },
    },
    {
      title: '认证方法',
      dataIndex: 'auth_keys',
      key: 'auth_keys',
      width: 200,
      render: (authKeys) => {
        if (!authKeys || authKeys.length === 0) return '-';
        return (
          <div>
            {authKeys.map((key) => (
              <Tag key={key} style={{ margin: '2px' }}>
                {key}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: '自动同步',
      dataIndex: 'schedule_enabled',
      key: 'schedule_enabled',
      width: 100,
      render: (enabled) => <Tag color={enabled ? 'green' : 'default'}>{enabled ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '同步间隔',
      dataIndex: 'schedule_interval_minutes',
      key: 'schedule_interval_minutes',
      width: 100,
      render: (interval) => (interval ? `${interval}分钟` : '-'),
    },
    {
      title: '同步状态',
      dataIndex: 'sync_status',
      key: 'sync_status',
      width: 100,
      render: (status) => {
        if (!status) return '-';
        const statusMap = {
          IDLE: { color: 'default', text: '空闲' },
          RUNNING: { color: 'processing', text: '运行中' },
          SUCCESS: { color: 'success', text: '成功' },
          FAILED: { color: 'error', text: '失败' },
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '最后同步',
      dataIndex: 'last_sync_at',
      key: 'last_sync_at',
      width: 180,
      render: (date) => (date ? moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditTopic(record)}>
            编辑
          </Button>
          <Button type="link" icon={record.schedule_enabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={() => (record.schedule_enabled ? handleDisableSync(record) : handleEnableSync(record))}>
            {record.schedule_enabled ? '停用同步' : '启用同步'}
          </Button>
          <Button type="link" icon={<SyncOutlined />} onClick={() => handleManualSync(record)} disabled={record.sync_status === 'RUNNING'}>
            手动同步
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteTopic(record)}>
            删除
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>交易话题管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTopic}>
          新建话题
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={topics}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />

      <Modal title={editingTopic ? '编辑交易话题' : '新建交易话题'} open={isModalVisible} onOk={handleModalOk} onCancel={handleModalCancel}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="话题名称" rules={[{ required: true, message: '请输入话题名称' }]}>
            <Input placeholder="请输入话题名称" />
          </Form.Item>

          <Form.Item name="trader_id" label="交易员" rules={[{ required: true, message: '请选择交易员' }]}>
            <Select placeholder="请选择交易员">
              {traderProfiles.map((trader) => (
                <Option key={trader.id} value={trader.id}>
                  {trader.display_name || `交易员 ${trader.id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="source_id" label="数据源" rules={[{ required: true, message: '请选择数据源' }]}>
            <Select placeholder="请选择数据源">
              {dataSources.map((dataSource) => (
                <Option key={dataSource.id} value={dataSource.id}>
                  {dataSource.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="external_topic_id" label="外部话题ID">
            <Input placeholder="请输入外部系统中的话题ID (可选)" />
          </Form.Item>

          <Form.Item name="description" label="话题描述">
            <Input.TextArea placeholder="请输入话题描述" rows={4} />
          </Form.Item>

          <Form.Item name="market_type" label="市场类型">
            <Select placeholder="请选择市场类型 (可选)">
              <Option value="US">美股</Option>
              <Option value="HK">港股</Option>
              <Option value="CN">A股</Option>
            </Select>
          </Form.Item>

          <Form.Item name="auth_keys" label="认证方法">
            <Select mode="multiple" placeholder="请选择认证方法 (可选)" allowClear>
              {authMethods.map((authMethod) => (
                <Option key={authMethod.id} value={authMethod.auth_key}>
                  {authMethod.auth_key} ({authMethod.auth_type})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="schedule_enabled" label="启用自动同步" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="schedule_interval_minutes" label="同步间隔（分钟）" rules={[{ required: true, message: '请输入同步间隔' }]}>
            <InputNumber min={1} max={1440} placeholder="请输入同步间隔（1-1440分钟）" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="status" label="状态" initialValue="ACTIVE">
            <Select>
              <Option value="ACTIVE">活跃</Option>
              <Option value="INACTIVE">非活跃</Option>
              <Option value="ARCHIVED">已归档</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleConfirmDelete} onCancel={handleCancelDelete} okText="确认删除" cancelText="取消" okType="danger">
        <p>确定要删除交易话题 "{deletingTopic?.name}" 吗？</p>
        <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该话题及其所有相关数据。</p>
      </Modal>
    </div>
  );
};

export default Topics;
